import { CheckCircle2, FileText, FileUser } from "lucide-react";

import { AIBadge, UploadZone } from "../../../lib/resumeUpload.js";
import { cn } from "../../../lib/utils.js";

export interface IntakeDocument {
  /** Name of the picked file, once one is attached. */
  fileName: string | null;
  loading: boolean;
  /** Result line under the zone — "✓ …" when the parse succeeded. */
  message: string;
  onFile: (file: File) => void;
}

function IntakeSlot({ icon: Icon, title, hint, doc, accept, extra }: {
  icon: typeof FileText;
  title: string;
  hint: string;
  doc: IntakeDocument;
  accept: string;
  /** Extra line below the status, e.g. the CTC the offer letter states. */
  extra?: string;
}) {
  const parsed = doc.message.startsWith("✓");
  return (
    <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-2 rounded-md border border-surface-200 bg-surface-50 p-3">
      <div className="flex items-center gap-2">
        <Icon size={15} className="shrink-0 text-surface-500" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{title}</span>
        {parsed && <CheckCircle2 size={15} className="shrink-0 text-[var(--color-success-fg)]" />}
      </div>
      <p className="text-[11px] leading-snug text-surface-500">{hint}</p>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <UploadZone
          label={doc.fileName ?? (doc.loading ? "Reading…" : "Choose file or drop here")}
          accept={accept}
          loading={doc.loading}
          onFile={doc.onFile}
        />
        <AIBadge loading={doc.loading} />
      </div>
      {doc.message && (
        <p className={cn("text-[11px] leading-snug", parsed ? "text-[var(--color-success-fg)]" : "text-[var(--color-warning-fg)]")}>
          {doc.message}
        </p>
      )}
      {extra && <p className="text-[11px] leading-snug text-surface-600">{extra}</p>}
    </div>
  );
}

// The two documents HR actually has at offer stage. Both are read by AI to
// prefill the form below; the offer letter is also stored on the employee once
// the hire is saved. Everything else — Aadhaar, PAN, bank proof — is uploaded by
// the employee through the onboarding wizard, behind the consent gate.
export function NewHireIntakeRow({ offerLetter, resume, ctcNote }: {
  offerLetter: IntakeDocument;
  resume: IntakeDocument;
  ctcNote: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex flex-wrap gap-3">
        <IntakeSlot
          icon={FileText}
          title="Offer Letter"
          hint="Fills designation, joining date, probation and notice period. PDF, image or Word."
          doc={offerLetter}
          accept="image/*,.pdf,.doc,.docx"
          {...(ctcNote ? { extra: ctcNote } : {})}
        />
        <IntakeSlot
          icon={FileUser}
          title="Résumé"
          hint="Fills personal email, phone, skills, education, work history and address. PDF or Word."
          doc={resume}
          accept=".pdf,.doc,.docx"
        />
      </div>
      <p className="mt-2 text-[11px] leading-snug text-surface-500">
        Both are optional — you can fill the form by hand. The employee uploads their Aadhaar, PAN and bank proof
        themselves from the onboarding link, after accepting the data-protection notice.
      </p>
    </div>
  );
}
